import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, MessageSquareReply, RefreshCw, Search } from 'lucide-react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  FeedbackAttachment,
  FeedbackPriority,
  FeedbackStatus,
  FeedbackTicket,
  getAdminFeedback,
  getFeedbackEvidenceBlob,
  replyToFeedback,
  updateAdminFeedback,
} from '../api/adminFeedback.api';

const STATUS_OPTIONS: FeedbackStatus[] = [
  'NEW',
  'TRIAGED',
  'IN_PROGRESS',
  'WAITING_FOR_USER',
  'RESOLVED',
  'CLOSED',
  'SPAM',
];
const PRIORITY_OPTIONS: FeedbackPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

const statusColor = (status: FeedbackStatus) => {
  if (status === 'NEW') return 'info';
  if (status === 'SPAM') return 'error';
  if (status === 'RESOLVED' || status === 'CLOSED') return 'success';
  return 'default';
};

const EvidencePreviewGrid: React.FC<{ attachments: FeedbackAttachment[] }> = ({ attachments }) => {
  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => attachment.mimeType.startsWith('image/')),
    [attachments]
  );
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (imageAttachments.length === 0) {
      setPreviewUrls({});
      return;
    }

    let cancelled = false;
    const objectUrls: string[] = [];
    setPreviewUrls({});

    void (async () => {
      const loadedUrls: Record<string, string> = {};

      for (const attachment of imageAttachments) {
        try {
          const blob = await getFeedbackEvidenceBlob(attachment.url);
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          objectUrls.push(objectUrl);
          loadedUrls[attachment.filePublicId] = objectUrl;
        } catch {
          // Keep the feedback detail usable if a single evidence preview fails.
        }
      }

      if (!cancelled) {
        setPreviewUrls(loadedUrls);
      }
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageAttachments]);

  if (imageAttachments.length === 0) return null;

  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {imageAttachments.map((attachment) => {
        const previewUrl = previewUrls[attachment.filePublicId];

        return (
          <button
            key={attachment.filePublicId}
            type="button"
            disabled={!previewUrl}
            onClick={() => previewUrl && window.open(previewUrl, '_blank', 'noopener,noreferrer')}
            title={attachment.fileName}
            className="group relative aspect-video overflow-hidden rounded-md border border-gray-200 bg-gray-100 text-left dark:border-slate-700 dark:bg-slate-900"
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={attachment.fileName}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-gray-500 dark:text-slate-400">
                Loading...
              </div>
            )}
            <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-2 py-1 text-xs text-white">
              {attachment.fileName}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const FeedbackManagementPage: React.FC = () => {
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<FeedbackTicket | null>(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const response = await getAdminFeedback({
        search: search.trim() || undefined,
        status: status || undefined,
        type: type || undefined,
        page: pagination.page + 1,
        limit: pagination.pageSize,
      });
      setTickets(response.feedback);
      setTotal(response.pagination.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, pagination.page, pagination.pageSize]);

  useEffect(() => {
    const handler = () => void loadFeedback();
    window.addEventListener('admin-feedback:changed', handler);
    return () => window.removeEventListener('admin-feedback:changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo<GridColDef<FeedbackTicket>[]>(
    () => [
      {
        field: 'title',
        headerName: 'Feedback',
        flex: 1,
        minWidth: 240,
        renderCell: (params) => (
          <button
            type="button"
            onClick={() => setSelectedTicket(params.row)}
            className="min-w-0 text-left"
          >
            <div className="truncate font-semibold text-gray-900 dark:text-white">
              {params.row.title || 'Untitled'}
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-slate-400">
              {params.row.user.email}
            </div>
          </button>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 150,
        renderCell: (params) => <Chip label={params.value} size="small" />,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={statusColor(params.value as FeedbackStatus)}
          />
        ),
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 120,
        renderCell: (params) => <Chip label={params.value || 'NORMAL'} size="small" />,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 180,
        valueFormatter: (value) => new Date(value as string).toLocaleString(),
      },
    ],
    []
  );

  const handleUpdate = async (data: { status?: FeedbackStatus; priority?: FeedbackPriority }) => {
    if (!selectedTicket) return;
    const updated = await updateAdminFeedback(selectedTicket.publicId, data);
    setSelectedTicket(updated);
    await loadFeedback();
  };

  const handleReply = async () => {
    if (!selectedTicket || !replyContent.trim()) return;
    const updated = await replyToFeedback(selectedTicket.publicId, replyContent.trim());
    setSelectedTicket(updated);
    setReplyContent('');
    setReplyDialogOpen(false);
    await loadFeedback();
  };

  return (
    <div className="min-h-screen min-w-0 bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <div className="mb-6 flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Feedback Management</h1>
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Review user feedback, reports, evidence, status, and replies.
        </p>
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_180px_180px_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void loadFeedback();
            }}
            placeholder="Search title, email, username..."
            className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(event) => setType(event.target.value)}
          className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">All types</option>
          <option value="FEEDBACK">Feedback</option>
          <option value="BUG_REPORT">Bug report</option>
          <option value="ABUSE_REPORT">Report</option>
          <option value="OTHER">Other</option>
        </select>
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          onClick={() => void loadFeedback()}
        >
          Refresh
        </Button>
      </div>

      <div className="rounded-lg bg-white shadow-sm dark:bg-slate-800" style={{ height: 650 }}>
        <DataGrid
          rows={tickets}
          getRowId={(row) => row.publicId}
          columns={columns}
          rowCount={total}
          loading={loading}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: 'none' }}
        />
      </div>

      <Drawer
        anchor="right"
        open={Boolean(selectedTicket)}
        onClose={() => setSelectedTicket(null)}
        PaperProps={{ className: 'w-full max-w-2xl bg-white dark:bg-slate-900' }}
      >
        {selectedTicket && (
          <div className="flex h-full min-h-0 flex-col">
            <div className="border-b border-gray-200 p-5 dark:border-slate-700">
              <div className="mb-3 flex items-center gap-2">
                <Chip label={selectedTicket.type} size="small" />
                <Chip
                  label={selectedTicket.status}
                  size="small"
                  color={statusColor(selectedTicket.status)}
                />
                <Chip label={selectedTicket.priority || 'NORMAL'} size="small" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedTicket.title || 'Untitled feedback'}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                {selectedTicket.user.email} · {new Date(selectedTicket.createdAt).toLocaleString()}
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                  Context
                </h3>
                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  {Object.entries(selectedTicket.meta || {}).map(([key, value]) => (
                    <div
                      key={key}
                      className="min-w-0 rounded-md bg-slate-100 p-2 dark:bg-slate-800"
                    >
                      <dt className="text-xs font-semibold text-gray-500 dark:text-slate-400">
                        {key}
                      </dt>
                      <dd className="truncate text-gray-900 dark:text-white">
                        {String(value || '')}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                  Conversation
                </h3>
                <div className="space-y-3">
                  {selectedTicket.messages.map((message) => (
                    <div
                      key={message.id}
                      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
                    >
                      <div className="mb-2 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
                        <span>{message.senderType}</span>
                        <span>{new Date(message.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-gray-900 dark:text-slate-100">
                        {message.content}
                      </div>
                      {message.attachments.length > 0 && (
                        <EvidencePreviewGrid attachments={message.attachments} />
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="border-t border-gray-200 p-5 dark:border-slate-700">
              <div className="mb-3 grid grid-cols-2 gap-3">
                <Select
                  size="small"
                  value={selectedTicket.status}
                  onChange={(event) =>
                    void handleUpdate({ status: event.target.value as FeedbackStatus })
                  }
                >
                  {STATUS_OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  value={selectedTicket.priority || 'NORMAL'}
                  onChange={(event) =>
                    void handleUpdate({ priority: event.target.value as FeedbackPriority })
                  }
                >
                  {PRIORITY_OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <Button
                fullWidth
                variant="contained"
                startIcon={<MessageSquareReply size={16} />}
                onClick={() => setReplyDialogOpen(true)}
              >
                Reply to User
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      <Dialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reply to user</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={replyContent}
            onChange={(event) => setReplyContent(event.target.value)}
            placeholder="Write a reply..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleReply()}>
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const AdminAgentTodoPage: React.FC = () => (
  <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
      <AlertCircle className="mb-3 text-amber-500" size={24} />
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Internal Agent</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
        TODO business riêng: admin chat agent, dry-run, approval, tool permissions, ban user, gửi
        mail, tổng hợp.
      </p>
    </div>
  </div>
);
